import { useState, useEffect } from 'react';
import { collection, query, orderBy, getDocs, where, Timestamp } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../config/firebase';
import { useAuth } from '../hooks/useAuth';
import {
  DocumentTextIcon,
  ArrowDownTrayIcon,
  CalendarIcon,
  ClockIcon,
  CheckCircleIcon,
  XCircleIcon,
  FunnelIcon,
  ExclamationCircleIcon,
  ArrowPathIcon,
} from '@heroicons/react/24/outline';

export default function Reports() {
  const { isMasterAdmin } = useAuth();
  const [reports, setReports] = useState([]);
  const [backfilling, setBackfilling] = useState(false);

  const handleBackfill = async () => {
    if (backfilling) return;
    const ok = window.confirm(
      'Scan storage and add missing Firestore rows for older PDFs?\n\n' +
        'This is safe to run multiple times — existing rows are skipped.',
    );
    if (!ok) return;
    setBackfilling(true);
    try {
      const fn = httpsCallable(functions, 'backfillReportsFromStorage');
      const res = await fn({});
      const { insertedCount, skippedCount } = res.data || {};
      alert(`✅ Backfill complete.\nInserted: ${insertedCount}\nAlready present: ${skippedCount}`);
      // Refresh table to pull the new rows.
      fetchReports();
    } catch (e) {
      console.error('backfillReportsFromStorage error:', e);
      alert(`❌ Backfill failed: ${e.message || e}`);
    } finally {
      setBackfilling(false);
    }
  };

  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // all, completed, failed
  const [typeFilter, setTypeFilter] = useState('all'); // all, daily, monthly
  const [dateRange, setDateRange] = useState('all'); // all, week, month, custom
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 20;

  useEffect(() => {
    fetchReports();
    setCurrentPage(1); // reset paging whenever the filter changes
  }, [filter, typeFilter, dateRange]);

  const fetchReports = async () => {
    try {
      setLoading(true);
      setError(null);
      
      let q;
      
      // Build query based on filters
      const constraints = [];
      
      // Add status filter if not 'all'
      if (filter !== 'all') {
        constraints.push(where('status', '==', filter));
      }
      
      // Add type filter if not 'all'
      if (typeFilter !== 'all') {
        constraints.push(where('type', '==', typeFilter));
      }
      
      // No server-side orderBy on generatedAt — Firestore excludes docs
      // that don't have the field, which was hiding older reports that
      // only carry reportDate. We sort client-side instead.
      if (constraints.length > 0) {
        q = query(collection(db, 'reports'), ...constraints);
      } else {
        q = query(collection(db, 'reports'));
      }

      const snapshot = await getDocs(q);
      let reportsData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      // Newest first, falling back through any timestamp the doc has.
      const tsMillis = (r) => {
        const t = r.generatedAt || r.reportDate || r.endDate;
        if (!t) return 0;
        if (typeof t.toMillis === 'function') return t.toMillis();
        if (typeof t === 'number') return t;
        if (typeof t === 'string') return new Date(t).getTime() || 0;
        return 0;
      };
      reportsData.sort((a, b) => tsMillis(b) - tsMillis(a));

      // Apply date range filter in memory. Use any available timestamp on
      // the document (generatedAt / reportDate) so old reports aren't
      // silently dropped here either.
      const reportDate = (r) => {
        const t = r.generatedAt || r.reportDate;
        if (!t) return null;
        if (typeof t.toDate === 'function') return t.toDate();
        if (typeof t === 'number') return new Date(t);
        if (typeof t === 'string') return new Date(t);
        return null;
      };
      if (dateRange === 'week') {
        const weekAgo = new Date();
        weekAgo.setDate(weekAgo.getDate() - 7);
        reportsData = reportsData.filter((r) => {
          const d = reportDate(r);
          return d && d >= weekAgo;
        });
      } else if (dateRange === 'month') {
        const monthAgo = new Date();
        monthAgo.setMonth(monthAgo.getMonth() - 1);
        reportsData = reportsData.filter((r) => {
          const d = reportDate(r);
          return d && d >= monthAgo;
        });
      }

      setReports(reportsData);
    } catch (error) {
      console.error('Error fetching reports:', error);
      setError('Failed to load reports. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async (report) => {
    try {
      // Open in new tab
      window.open(report.fileUrl, '_blank');
    } catch (error) {
      console.error('Error downloading report:', error);
      alert('Failed to open report. Please try again.');
    }
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatDateTime = (timestamp) => {
    if (!timestamp) return 'N/A';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      completed: {
        bg: 'bg-green-100',
        text: 'text-green-800',
        icon: CheckCircleIcon,
        label: 'Completed'
      },
      failed: {
        bg: 'bg-red-100',
        text: 'text-red-800',
        icon: XCircleIcon,
        label: 'Failed'
      }
    };

    const config = statusConfig[status] || statusConfig.completed;
    const Icon = config.icon;

    return (
      <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium ${config.bg} ${config.text}`}>
        <Icon className="w-4 h-4" />
        {config.label}
      </span>
    );
  };

  const getTypeBadge = (type) => {
    const typeConfig = {
      daily: {
        bg: 'bg-blue-100',
        text: 'text-blue-800',
        label: 'Daily'
      },
      monthly: {
        bg: 'bg-purple-100',
        text: 'text-purple-800',
        label: 'Monthly'
      }
    };

    const config = typeConfig[type] || typeConfig.daily;

    return (
      <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${config.bg} ${config.text}`}>
        {config.label}
      </span>
    );
  };

  const stats = {
    total: reports.length,
    completed: reports.filter(r => r.status === 'completed').length,
    failed: reports.filter(r => r.status === 'failed').length,
    daily: reports.filter(r => r.type === 'daily').length,
    monthly: reports.filter(r => r.type === 'monthly').length
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Reports</h1>
          <p className="text-gray-600 mt-1">View and download automated daily and monthly reports</p>
        </div>
        {isMasterAdmin && (
          <button
            onClick={handleBackfill}
            disabled={backfilling}
            title="Index older PDFs from Storage that don't have a Firestore row yet"
            className="inline-flex items-center gap-2 px-3 py-2 bg-gray-100 hover:bg-gray-200 border border-gray-200 rounded-lg text-sm font-medium disabled:opacity-60"
          >
            <ArrowPathIcon className={`w-4 h-4 ${backfilling ? 'animate-spin' : ''}`} />
            {backfilling ? 'Indexing…' : 'Index older PDFs'}
          </button>
        )}
      </div>

      {/* Error Alert */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex items-center gap-3">
          <ExclamationCircleIcon className="w-6 h-6 text-red-600 flex-shrink-0" />
          <div>
            <p className="text-sm font-medium text-red-800">{error}</p>
          </div>
          <button
            onClick={() => setError(null)}
            className="ml-auto text-red-600 hover:text-red-800"
          >
            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
            </svg>
          </button>
        </div>
      )}

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-6">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Total Reports</p>
              <p className="text-3xl font-bold text-gray-900 mt-2">{stats.total}</p>
            </div>
            <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
              <DocumentTextIcon className="w-6 h-6 text-blue-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Completed</p>
              <p className="text-3xl font-bold text-green-600 mt-2">{stats.completed}</p>
            </div>
            <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
              <CheckCircleIcon className="w-6 h-6 text-green-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Failed</p>
              <p className="text-3xl font-bold text-red-600 mt-2">{stats.failed}</p>
            </div>
            <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center">
              <XCircleIcon className="w-6 h-6 text-red-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Daily</p>
              <p className="text-3xl font-bold text-blue-600 mt-2">{stats.daily}</p>
            </div>
            <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
              <CalendarIcon className="w-6 h-6 text-blue-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Monthly</p>
              <p className="text-3xl font-bold text-purple-600 mt-2">{stats.monthly}</p>
            </div>
            <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
              <CalendarIcon className="w-6 h-6 text-purple-600" />
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
        <div className="flex items-center gap-4 flex-wrap">
          <div className="flex items-center gap-2">
            <FunnelIcon className="w-5 h-5 text-gray-400" />
            <span className="text-sm font-medium text-gray-700">Filters:</span>
          </div>

          {/* Type Filter */}
          <select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="all">All Types</option>
            <option value="daily">Daily Reports</option>
            <option value="monthly">Monthly Reports</option>
          </select>

          {/* Status Filter */}
          <select
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="all">All Status</option>
            <option value="completed">Completed</option>
            <option value="failed">Failed</option>
          </select>

          {/* Date Range Filter */}
          <select
            value={dateRange}
            onChange={(e) => setDateRange(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="all">All Time</option>
            <option value="week">Last 7 Days</option>
            <option value="month">Last 30 Days</option>
          </select>

          <button
            onClick={fetchReports}
            className="ml-auto px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors"
          >
            Refresh
          </button>
        </div>
      </div>

      {/* Reports Table */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        {loading ? (
          <div className="p-12 text-center">
            <div className="inline-block w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
            <p className="text-gray-600 mt-4">Loading reports...</p>
          </div>
        ) : reports.length === 0 ? (
          <div className="p-12 text-center">
            <DocumentTextIcon className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-600 font-medium">No reports found</p>
            <p className="text-gray-500 text-sm mt-2">Reports will appear here automatically</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Report Date
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Generated At
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Type
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {reports
                  .slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage)
                  .map((report) => (
                  <tr key={report.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        <CalendarIcon className="w-5 h-5 text-gray-400" />
                        <div>
                          <p className="text-sm font-medium text-gray-900">
                            {formatDate(report.reportDate)}
                          </p>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        <ClockIcon className="w-5 h-5 text-gray-400" />
                        <span className="text-sm text-gray-600">
                          {formatDateTime(report.generatedAt)}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {getTypeBadge(report.type || 'daily')}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {getStatusBadge(report.status)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {report.status === 'completed' && report.fileUrl ? (
                        <button
                          onClick={() => handleDownload(report)}
                          className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors"
                        >
                          <ArrowDownTrayIcon className="w-4 h-4" />
                          Download
                        </button>
                      ) : report.status === 'failed' ? (
                        <div className="text-sm text-red-600">
                          <p className="font-medium">Generation Failed</p>
                          {report.error && (
                            <p className="text-xs text-gray-500 mt-1">{report.error}</p>
                          )}
                        </div>
                      ) : (
                        <span className="text-sm text-gray-500">N/A</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {(() => {
              const totalPages = Math.max(1, Math.ceil(reports.length / itemsPerPage));
              if (reports.length <= itemsPerPage) return null;
              const startIndex = (currentPage - 1) * itemsPerPage + 1;
              const endIndex = Math.min(currentPage * itemsPerPage, reports.length);
              const goTo = (p) => setCurrentPage(Math.min(Math.max(1, p), totalPages));
              // Show up to 7 page buttons centered around currentPage.
              const windowSize = 7;
              let pageStart = Math.max(1, currentPage - Math.floor(windowSize / 2));
              let pageEnd = pageStart + windowSize - 1;
              if (pageEnd > totalPages) {
                pageEnd = totalPages;
                pageStart = Math.max(1, pageEnd - windowSize + 1);
              }
              const pages = [];
              for (let i = pageStart; i <= pageEnd; i++) pages.push(i);
              return (
                <div className="flex items-center justify-between px-6 py-4 border-t border-gray-200 bg-gray-50">
                  <p className="text-sm text-gray-600">
                    Showing{' '}
                    <span className="font-semibold">{startIndex}</span>–
                    <span className="font-semibold">{endIndex}</span> of{' '}
                    <span className="font-semibold">{reports.length}</span>
                  </p>
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => goTo(1)}
                      disabled={currentPage === 1}
                      className="px-2 py-1 text-sm rounded border border-gray-200 disabled:opacity-40 hover:bg-white"
                    >
                      «
                    </button>
                    <button
                      onClick={() => goTo(currentPage - 1)}
                      disabled={currentPage === 1}
                      className="px-2 py-1 text-sm rounded border border-gray-200 disabled:opacity-40 hover:bg-white"
                    >
                      ‹
                    </button>
                    {pages.map((p) => (
                      <button
                        key={p}
                        onClick={() => goTo(p)}
                        className={`px-3 py-1 text-sm rounded border ${
                          p === currentPage
                            ? 'bg-blue-600 text-white border-blue-600'
                            : 'border-gray-200 hover:bg-white'
                        }`}
                      >
                        {p}
                      </button>
                    ))}
                    <button
                      onClick={() => goTo(currentPage + 1)}
                      disabled={currentPage === totalPages}
                      className="px-2 py-1 text-sm rounded border border-gray-200 disabled:opacity-40 hover:bg-white"
                    >
                      ›
                    </button>
                    <button
                      onClick={() => goTo(totalPages)}
                      disabled={currentPage === totalPages}
                      className="px-2 py-1 text-sm rounded border border-gray-200 disabled:opacity-40 hover:bg-white"
                    >
                      »
                    </button>
                  </div>
                </div>
              );
            })()}
          </div>
        )}
      </div>
    </div>
  );
}